<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { formatDistanceToNow, format } from 'date-fns'
import { track } from '@/lib/usageTracker'
import { Button, Badge, Spinner } from '@/components/ui'
import { X, Clock, RefreshCw, AlertTriangle, RotateCcw, Ban } from 'lucide-vue-next'
import {
  cancelTask,
  retryTask,
  getTaskChildren,
  type TaskDetail,
  type TaskSummaryDto,
} from '@/api/lytAccountingClient'
import { useTaskActivityStore } from '@/stores/taskActivityStore'
type TaskLike = Partial<TaskDetail & TaskSummaryDto> & { taskId?: number | string }
type DrawerState = 'idle' | 'loading' | 'ready' | 'auth' | 'not-found' | 'stale' | 'error'
const props = defineProps<{
  task?: TaskLike | null
  open?: boolean
}>()

const emit = defineEmits<{
  close: []
  'action-complete': [taskId: number | string]
}>()

const taskActivity = useTaskActivityStore()
const detail = ref<TaskDetail | null>(null)
const state = ref<DrawerState>('idle')
const errorMessage = ref('')
const actionMessage = ref('')
const actionError = ref('')
const actionLoading = ref<'retry' | 'cancel' | null>(null)
let detailRequest = 0

// --- Child task list (tasks-accordion) ---
// Fetches /api/v1/tasks/{id}/children whenever the loaded detail has
// totalChildren > 0. The request-id guard prevents stale responses
// from a previous parent from overwriting a newer selection.
const children = ref<TaskSummaryDto[]>([])
const childrenLoading = ref(false)
let childrenRequest = 0
const displayTask = computed<TaskLike | TaskDetail | null>(() => detail.value ?? props.task ?? null)
const terminalStatuses = ['completed', 'completed_with_errors', 'failed', 'cancelled', 'dead_lettered']
const canRetry = computed(() => detail.value?.status === 'dead_lettered')
const canCancel = computed(() => Boolean(detail.value?.status && !terminalStatuses.includes(detail.value.status)))

const statusVariant = (status?: string): 'default' | 'success' | 'error' | 'warning' => {
  switch (status) {
    case 'completed': return 'success'
    case 'completed_with_errors': return 'warning'
    case 'failed': return 'error'
    case 'dead_lettered': return 'error'
    case 'queued':
    case 'waiting':
    case 'leased':
    case 'running':
    case 'in_progress': return 'warning'
    default: return 'default'
  }
}

const formatAbsolute = (dateStr?: string | null) => {
  if (!dateStr) return 'N/A'
  return format(new Date(dateStr), "MMM d, yyyy 'at' h:mm a")
}

const formatRelative = (dateStr?: string | null) => {
  if (!dateStr) return 'N/A'
  return formatDistanceToNow(new Date(dateStr), { addSuffix: true })
}

const fieldValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '-'
  return String(value)
}

const counterValue = (value: number | null | undefined) => value ?? 0

function classifyError(err: unknown): DrawerState {
  const message = (err as Error)?.message || 'Request failed'
  errorMessage.value = message
  if (message.startsWith('HTTP 401') || message.startsWith('HTTP 403')) return 'auth'
  if (message.startsWith('HTTP 404')) return 'not-found'
  return 'error'
}

async function loadDetail() {
  actionMessage.value = ''
  actionError.value = ''
  const taskId = props.task?.taskId
  if (!props.open) return
  if (taskId === null || taskId === undefined) {
    detail.value = null
    state.value = 'stale'
    return
  }

  const requestId = ++detailRequest
  state.value = 'loading'
  errorMessage.value = ''

  try {
    const loaded = await taskActivity.loadTaskDetail(taskId)
    if (requestId !== detailRequest) return
    if (String(loaded.taskId) !== String(taskId)) {
      detail.value = null
      state.value = 'stale'
      return
    }
    detail.value = loaded
    state.value = 'ready'
  } catch (err) {
    if (requestId !== detailRequest) return
    detail.value = null
    state.value = classifyError(err)
  }
}

// --- Child task fetch (tasks-accordion) ---
async function loadChildren() {
  if (!detail.value || !detail.value.totalChildren) {
    children.value = []
    return
  }
  const requestId = ++childrenRequest
  childrenLoading.value = true
  try {
    const res = await getTaskChildren(detail.value.taskId)
    if (requestId !== childrenRequest) return
    children.value = res.tasks
  } catch {
    if (requestId === childrenRequest) children.value = []
  } finally {
    if (requestId === childrenRequest) childrenLoading.value = false
  }
}

watch(detail, () => { void loadChildren() }, { immediate: true })

async function runAction(action: 'retry' | 'cancel') {
  const taskId = props.task?.taskId
  if (taskId === null || taskId === undefined) return
  actionMessage.value = ''
  actionError.value = ''

  try {
    const result = action === 'retry'
      ? await retryTask(taskId)
      : await cancelTask(taskId)
    actionMessage.value = result.message
    detail.value = await taskActivity.loadTaskDetail(taskId, true)
    state.value = 'ready'
    emit('action-complete', taskId)
  } catch (err) {
    actionError.value = (err as Error)?.message || `Failed to ${action} task`
  } finally {
    actionLoading.value = null
  }
}

watch(
  () => [props.open, props.task?.taskId],
  () => {
    if (props.open) {
      loadDetail()
    } else {
      detail.value = null
      state.value = 'idle'
      actionMessage.value = ''
      actionError.value = ''
    }
  },
  { immediate: true },
)
</script>

<template>
  <Transition name="drawer">
    <div v-if="open" class="drawer-overlay" @click.self="emit('close')">
      <div class="drawer-panel">
        <div class="drawer-header">
          <div class="drawer-title-group">
            <span class="drawer-eyebrow">Task detail</span>
            <h3 class="drawer-title">{{ fieldValue(props.task?.taskId) }}</h3>
          </div>
          <button class="close-btn" @click="emit('close')" aria-label="Close drawer">
            <X class="close-icon" />
          </button>
        </div>

        <div v-if="displayTask" class="status-highlight">
          <Badge :variant="statusVariant(displayTask.status)" size="md">
            {{ displayTask.status || 'unknown' }}
          </Badge>
          <span class="entity-pill">{{ displayTask.entityType || 'Task' }}</span>
        </div>
        <div v-if="state === 'loading'" class="drawer-state">
          <RefreshCw class="state-icon spin" />
          <span>Loading task detail...</span>
        </div>

        <div v-else-if="state === 'auth'" class="drawer-state drawer-state--error">
          <AlertTriangle class="state-icon" />
          <span>Not authorized to inspect this task.</span>
        </div>

        <div v-else-if="state === 'not-found'" class="drawer-state drawer-state--error">
          <AlertTriangle class="state-icon" />
          <span>Task detail was not found.</span>
        </div>

        <div v-else-if="state === 'stale'" class="drawer-state drawer-state--error">
          <AlertTriangle class="state-icon" />
          <span>Task selection is stale. Refresh the list and try again.</span>
        </div>

        <div v-else-if="state === 'error'" class="drawer-state drawer-state--error">
          <AlertTriangle class="state-icon" />
          <span>{{ errorMessage }}</span>
        </div>

        <template v-else-if="detail">
          <dl class="detail-list">
            <div class="detail-row">
              <dt class="detail-label"><Clock class="detail-icon" />Entity ID</dt>
              <dd class="detail-value mono">{{ fieldValue(detail.entityId || detail.externalId) }}</dd>
            </div>

            <div class="detail-row">
              <dt class="detail-label">Operation</dt>
              <dd class="detail-value">{{ fieldValue(detail.operation) }}</dd>
            </div>

            <div class="detail-row">
              <dt class="detail-label">Scope</dt>
              <dd class="detail-value">{{ fieldValue(detail.scope) }}</dd>
            </div>

            <div class="detail-row">
              <dt class="detail-label">Parent</dt>
              <dd class="detail-value mono">{{ fieldValue(detail.parentTaskId) }}</dd>
            </div>

            <div class="detail-row">
              <dt class="detail-label"><RefreshCw class="detail-icon" />Attempts</dt>
              <dd class="detail-value">
                {{ fieldValue(detail.attemptCount) }} / {{ fieldValue(detail.maxAttempts) }}
              </dd>
            </div>

            <div class="detail-row">
              <dt class="detail-label">Dedupe key</dt>
              <dd class="detail-value mono">{{ fieldValue(detail.dedupeKey) }}</dd>
            </div>

            <div class="detail-row">
              <dt class="detail-label">Accepted</dt>
              <dd class="detail-value">
                <span :title="formatAbsolute(detail.acceptedAt)">{{ formatRelative(detail.acceptedAt) }}</span>
              </dd>
            </div>

            <div class="detail-row">
              <dt class="detail-label">Started</dt>
              <dd class="detail-value">
                <span :title="formatAbsolute(detail.startedAt)">{{ formatRelative(detail.startedAt) }}</span>
              </dd>
            </div>

            <div class="detail-row">
              <dt class="detail-label">Updated</dt>
              <dd class="detail-value">
                <span :title="formatAbsolute(detail.updatedAt)">{{ formatRelative(detail.updatedAt) }}</span>
              </dd>
            </div>

            <div class="detail-row">
              <dt class="detail-label">Finished</dt>
              <dd class="detail-value">
                <span :title="formatAbsolute(detail.finishedAt)">{{ formatRelative(detail.finishedAt) }}</span>
              </dd>
            </div>
          </dl>

          <div class="counter-section">
            <div class="counter-title">Child counters</div>
            <div class="counter-grid">
              <div class="counter-cell"><span>Total</span><strong>{{ counterValue(detail.totalChildren) }}</strong></div>
              <div class="counter-cell"><span>Queued</span><strong>{{ counterValue(detail.queuedChildren) }}</strong></div>
              <div class="counter-cell"><span>Running</span><strong>{{ counterValue(detail.runningChildren) }}</strong></div>
              <div class="counter-cell"><span>Done</span><strong>{{ counterValue(detail.completedChildren) }}</strong></div>
              <div class="counter-cell"><span>Failed</span><strong>{{ counterValue(detail.failedChildren) }}</strong></div>
              <div class="counter-cell"><span>Cancelled</span><strong>{{ counterValue(detail.cancelledChildren) }}</strong></div>
              <div class="counter-cell"><span>Skipped</span><strong>{{ counterValue(detail.skippedChildren) }}</strong></div>
            </div>
          </div>

          <div v-if="detail.totalChildren" class="counter-section">
            <div class="counter-title">Child tasks</div>
            <Spinner v-if="childrenLoading" />
            <ul v-else-if="children.length" class="child-list">
              <li v-for="child in children" :key="child.taskId" class="child-list-row">
                <Badge :variant="statusVariant(child.status)" size="sm">{{ child.status }}</Badge>
                <span class="child-id mono">#{{ child.taskId }}</span>
                <span class="child-entity">{{ child.entityType }}</span>
                <span class="child-ext">{{ child.externalId ?? '—' }}</span>
              </li>
            </ul>
            <p v-else class="child-list-empty">No child tasks recorded.</p>
          </div>
          <div v-if="detail.lastErrorCode || detail.lastErrorMessage" class="error-row">
            <dt class="detail-label"><AlertTriangle class="detail-icon" />Last error</dt>
            <dd class="detail-value error-value">
              <span v-if="detail.lastErrorCode" class="mono">{{ detail.lastErrorCode }}</span>
              <span v-if="detail.lastErrorMessage">{{ detail.lastErrorMessage }}</span>
            </dd>
          </div>
        </template>

        <div v-if="actionMessage" class="action-message">{{ actionMessage }}</div>
        <div v-if="actionError" class="action-message action-message--error">{{ actionError }}</div>

        <div class="drawer-footer">
          <Button
            v-if="canRetry"
            variant="secondary"
            size="sm"
            :loading="actionLoading === 'retry'"
            :disabled="Boolean(actionLoading)"
            @click="() => { track('btn:RetryTaskDetail', 'click'); runAction('retry') }"
          >
            <RotateCcw class="h-4 w-4" />
            Retry
          </Button>
          <Button
            v-if="canCancel"
            variant="destructive"
            size="sm"
            :loading="actionLoading === 'cancel'"
            :disabled="Boolean(actionLoading)"
            @click="() => { track('btn:CancelTaskDetail', 'click'); runAction('cancel') }"
          >
            <Ban class="h-4 w-4" />
            Cancel
          </Button>
          <Button variant="secondary" size="sm" @click="emit('close')">
            Close
          </Button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(21, 32, 51, 0.45);
  z-index: 50;
  display: flex;
  justify-content: flex-end;
}

.drawer-panel {
  width: 440px;
  max-width: 100vw;
  height: 100%;
  background: var(--color-surface);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 20px rgba(21, 32, 51, 0.12);
}

.drawer-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid var(--color-border-soft);
  background: var(--color-surface-muted);
  gap: 0.75rem;
}

.drawer-title-group {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}

.drawer-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-ink-soft);
}

.drawer-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--color-ink);
  font-family: 'SF Mono', 'Fira Code', monospace;
  margin: 0;
  overflow-wrap: anywhere;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-ink-soft);
  padding: 0.2rem;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.12s, background 0.12s;
  flex-shrink: 0;
}

.close-btn:hover {
  color: var(--color-ink);
  background: var(--color-border-soft);
}

.close-icon {
  width: 1rem;
  height: 1rem;
}

.status-highlight {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.9rem 1.25rem;
  border-bottom: 1px solid var(--color-border-soft);
}

.entity-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.18rem 0.55rem;
  background: var(--color-surface-muted);
  border: 1px solid var(--color-border-soft);
  border-radius: var(--radius-sm);
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--color-ink-soft);
}

.drawer-state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  font-size: 0.82rem;
  color: var(--color-ink-soft);
}

.drawer-state--error {
  color: var(--color-error);
}

.state-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.spin {
  animation: spin 1s linear infinite;
}

.detail-list {
  padding: 0.75rem 0;
  display: flex;
  flex-direction: column;
}

.detail-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.6rem 1.25rem;
}

.detail-row:hover {
  background: var(--color-surface-muted);
}

.detail-label {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-ink-soft);
  flex-shrink: 0;
}

.detail-icon {
  width: 0.85rem;
  height: 0.85rem;
  flex-shrink: 0;
}

.detail-value {
  font-size: 0.82rem;
  color: var(--color-ink);
  text-align: right;
  word-break: break-word;
}

.mono {
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.75rem;
}

.counter-section {
  padding: 0.8rem 1.25rem 1rem;
  border-top: 1px solid var(--color-border-soft);
}

.counter-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-ink-soft);
  margin-bottom: 0.55rem;
}

.counter-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.45rem;
}

.counter-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.45rem 0.55rem;
  border: 1px solid var(--color-border-soft);
  border-radius: var(--radius-sm);
  background: var(--color-surface-muted);
  font-size: 0.76rem;
}

.counter-cell span {
  color: var(--color-ink-soft);
}

.counter-cell strong {
  color: var(--color-ink);
  font-weight: 700;
}

/* --- Child task list (tasks-accordion) --- */
.child-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.child-list-row {
  display: grid;
  grid-template-columns: 6.5rem 4.5rem 1fr 1fr;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.5rem;
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  font-size: 0.75rem;
  color: var(--color-ink);
}

.child-id {
  font-family: monospace;
  font-size: 0.7rem;
  color: var(--color-ink-soft);
}

.child-entity {
  font-weight: 600;
}

.child-ext {
  color: var(--color-ink-soft);
  font-size: 0.7rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.child-list-empty {
  margin: 0;
  font-size: 0.75rem;
  color: var(--color-ink-soft);
}

.error-row {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0.75rem 1.25rem;
  margin: 0.25rem 0;
  background: rgba(239, 68, 68, 0.04);
  border-top: 1px solid rgba(239, 68, 68, 0.12);
  border-bottom: 1px solid rgba(239, 68, 68, 0.12);
}

.error-value {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  color: var(--color-error);
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.4;
  text-align: left;
}

.action-message {
  margin: 0.75rem 1.25rem 0;
  padding: 0.55rem 0.7rem;
  border: 1px solid var(--color-border-soft);
  border-radius: var(--radius-sm);
  background: var(--color-surface-muted);
  font-size: 0.78rem;
  color: var(--color-ink);
}

.action-message--error {
  color: var(--color-error);
  background: rgba(239, 68, 68, 0.04);
  border-color: rgba(239, 68, 68, 0.18);
}

.drawer-footer {
  margin-top: auto;
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--color-border-soft);
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.2s ease;
}

.drawer-enter-active .drawer-panel,
.drawer-leave-active .drawer-panel {
  transition: transform 0.25s ease;
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}

.drawer-enter-from .drawer-panel,
.drawer-leave-to .drawer-panel {
  transform: translateX(100%);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 480px) {
  .drawer-panel {
    width: 100vw;
  }
}
</style>
